import type { Notification } from '@tandemone/types'
import type { QueryClient } from '@tanstack/react-query'
import type { Socket } from 'socket.io-client'

import { setAppBadge } from './badging'
import { NOTIFICATIONS_FEED_KEY } from './use-notifications'
import { UNREAD_COUNT_KEY } from './use-unread-count'

/** wire socket events from the api to TanStack Query cache mutations.
 * called once at app boot after the user signs in (so the socket has a
 * cookie to authenticate with). returns a cleanup fn that detaches the
 * listeners. useful on logout.
 */
export function installNotificationSocketHandlers(
  socket: Socket,
  queryClient: QueryClient,
  onToast: (notification: Notification) => void
): () => void {
  function onNew(event: {
    notification: Notification
    showToast: boolean
  }): void {
    /** ignore notifications addressed to a company other than the active one
     * (account-level rows carry null and always apply), so the feed, badge,
     * and toast all stay scoped. the active company id lives in the setup
     * query cache.
     */
    const activeCompanyId =
      queryClient.getQueryData<{ companyId: string | null }>(['setup'])
        ?.companyId ?? null
    const { companyId } = event.notification
    if (companyId !== null && companyId !== activeCompanyId) return

    /** refetch the scoped count + feed rather than optimistically bumping.
     * the scoped endpoints return the right values.
     */
    void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
    void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_FEED_KEY })
    // badging api: only meaningful for installed PWAs, no-op elsewhere
    setAppBadge(queryClient)
    if (event.showToast) onToast(event.notification)
  }

  function onRead({ id: _id }: { id: string }): void {
    queryClient.setQueryData<{ count: number } | undefined>(
      UNREAD_COUNT_KEY,
      old => ({ count: Math.max(0, (old?.count ?? 0) - 1) })
    )
    void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_FEED_KEY })
    setAppBadge(queryClient)
  }

  function onDismissed({ id: _id }: { id: string }): void {
    void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_FEED_KEY })
    void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
    setAppBadge(queryClient)
  }

  function onUnreadCountChanged(): void {
    /** the broadcast carries an unscoped total, refetch the active-company-
     * scoped count instead of trusting it.
     */
    void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
    void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_FEED_KEY })
    setAppBadge(queryClient)
  }

  function onConnect(): void {
    /** on reconnect (or initial connect), invalidate caches so any events
     * we missed during the offline window get backfilled.
     */
    void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
    void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_FEED_KEY })
  }

  socket.on('notification:new', onNew)
  socket.on('notification:read', onRead)
  socket.on('notification:dismissed', onDismissed)
  socket.on('notification:unread_count_changed', onUnreadCountChanged)
  socket.on('connect', onConnect)

  return () => {
    socket.off('notification:new', onNew)
    socket.off('notification:read', onRead)
    socket.off('notification:dismissed', onDismissed)
    socket.off('notification:unread_count_changed', onUnreadCountChanged)
    socket.off('connect', onConnect)
  }
}
